import { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, ExternalLink, GitCompareArrows } from 'lucide-react';

import { useGetTaskAdvertsQuery } from '../../../../redux/services/parser/parserApi';
import { Dialog } from '../../Dialog';
import { Loader } from '../../Loader';
import { AdvertDiffModal } from '../AdvertDiffModal';

const PAGE_SIZE = 25;

const formatPrice = value => {
  if (value == null) return '—';
  try {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return `€${value}`;
  }
};

const formatDate = value => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    });
  } catch {
    return value;
  }
};

const formatDateTime = value => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return value;
  }
};

const truncate = (value, max = 60) => {
  if (!value) return '—';
  return value.length > max ? `${value.slice(0, max - 1)}…` : value;
};

const locationLabel = advert => {
  const parts = [advert.city, advert.country].filter(Boolean);
  if (parts.length > 0) return parts.join(', ');
  return advert.location || '—';
};

const TABS = [
  { key: 'Added',   label: 'Added',   verb: 'added',   countKey: 'advertsCount' },
  { key: 'Changed', label: 'Changed', verb: 'changed', countKey: 'changedCount' },
  { key: 'all',     label: 'All',     verb: 'touched', countKey: null },
];

export const TaskAdvertsModal = ({ isOpen, onClose, token, task, initialEventType = 'Added' }) => {
  const [page, setPage] = useState(1);
  const [eventType, setEventType] = useState(initialEventType);
  const [openDiff, setOpenDiff] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setPage(1);
      setEventType(initialEventType);
      setOpenDiff(null);
    }
  }, [isOpen, task?.id, initialEventType]);

  useEffect(() => {
    setOpenDiff(null);
  }, [eventType, page]);

  const { data, isLoading, isFetching, error } = useGetTaskAdvertsQuery(
    { token, taskId: task?.id, perPage: PAGE_SIZE, page, eventType },
    { skip: !isOpen || !token || !task?.id }
  );

  const adverts = data?.data ?? [];
  const meta = data?.meta ?? { total: 0, per_page: PAGE_SIZE, current_page: page, last_page: 1 };
  const total = meta.total ?? 0;
  const lastPage = Math.max(meta.last_page ?? 1, 1);
  const currentPage = meta.current_page ?? page;
  const from = total === 0 ? 0 : (currentPage - 1) * meta.per_page + 1;
  const to = Math.min(currentPage * meta.per_page, total);

  const handlePrev = () => setPage(p => Math.max(1, p - 1));
  const handleNext = () => setPage(p => Math.min(lastPage, p + 1));

  const activeTab = TABS.find(t => t.key === eventType) ?? TABS[0];
  const combinedCount = (task?.advertsCount ?? 0) + (task?.changedCount ?? 0);
  const tabBadgeCount = tab => {
    if (tab.countKey === null) return combinedCount;
    return task?.[tab.countKey] ?? 0;
  };
  const switchTab = key => {
    if (key === eventType) return;
    setEventType(key);
    setPage(1);
  };

  const content = (
    <div
      data-testid="task-adverts-modal"
      className="flex flex-col w-[92vw] max-w-5xl max-h-[80vh] text-left"
    >
      <div className="flex flex-wrap items-start justify-between gap-3 pr-10 pb-3 border-b border-gray-200 flex-shrink-0">
        <div className="min-w-0">
          <h2 id="modal-title" className="text-lg font-semibold text-blackColor truncate">
            Adverts {activeTab.verb} by <span className="text-purpleColor">{task?.source || 'unknown spider'}</span>
          </h2>
          <p className="mt-1 text-xs text-darkGrayColor">
            Task #{task?.id}
            {task?.session_id ? ` · Session #${task.session_id}` : ''}
            {' · '}
            {total.toLocaleString()} {total === 1 ? 'advert' : 'adverts'} {activeTab.verb} during this spider run
          </p>
        </div>
        {isFetching && !isLoading && (
          <span className="text-xs text-darkGrayColor">refreshing…</span>
        )}
      </div>

      <div
        role="tablist"
        aria-label="Filter by event type"
        data-testid="task-adverts-tabs"
        className="flex items-center gap-1 border-b border-gray-200 mt-2 flex-shrink-0"
      >
        {TABS.map(tab => {
          const isActive = tab.key === eventType;
          const count = tabBadgeCount(tab);
          return (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={isActive}
              data-testid={`task-adverts-tab-${tab.key.toLowerCase()}`}
              onClick={() => switchTab(tab.key)}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 -mb-px transition ${
                isActive
                  ? 'border-purpleColor text-purpleColor'
                  : 'border-transparent text-darkGrayColor hover:text-blackColor hover:border-gray-300'
              }`}
            >
              {tab.label}
              <span
                className={`inline-flex items-center px-1.5 rounded-full text-[10px] tabular-nums ${
                  isActive ? 'bg-purpleColor/15 text-purpleColor' : 'bg-lightGrayColor text-darkGrayColor'
                }`}
              >
                {count.toLocaleString()}
              </span>
            </button>
          );
        })}
      </div>

      {isLoading && (
        <div className="flex items-center justify-center h-80">
          <Loader />
        </div>
      )}

      {!isLoading && error && (
        <div
          data-testid="task-adverts-error"
          className="flex flex-col items-center justify-center h-80 text-sm text-redColor gap-2"
        >
          <span>Failed to load adverts for this task.</span>
          <span className="text-xs text-darkGrayColor">
            {error?.status ? `HTTP ${error.status}` : 'Network or server error'}
          </span>
        </div>
      )}

      {!isLoading && !error && total === 0 && (
        <div
          data-testid="task-adverts-empty"
          className="flex items-center justify-center h-80 text-sm text-darkGrayColor"
        >
          This spider run did not {activeTab.verb === 'touched' ? 'add or change' : activeTab.verb} any adverts.
        </div>
      )}

      {!isLoading && !error && total > 0 && (
        <>
          <div className="flex-1 min-h-0 overflow-auto pt-3">
            <table className="w-full text-left text-xs" data-testid="task-adverts-table">
              <thead className="sticky top-0 bg-white z-10 border-b border-gray-200 text-darkGrayColor uppercase tracking-wide">
                <tr>
                  <th className="py-2 pr-3 font-medium">#</th>
                  {eventType === 'all' && <th className="py-2 pr-3 font-medium">Event</th>}
                  <th className="py-2 pr-3 font-medium">Title</th>
                  <th className="py-2 pr-3 font-medium">Location</th>
                  <th className="py-2 pr-3 font-medium text-right">Price</th>
                  <th className="py-2 pr-3 font-medium">Published</th>
                  <th className="py-2 pr-3 font-medium">Event at</th>
                  {(eventType === 'Changed' || eventType === 'all') && (
                    <th className="py-2 pr-3 font-medium">Diff</th>
                  )}
                  <th className="py-2 pr-0 font-medium">URL</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {adverts.map(advert => {
                  const isChanged = advert.event_type === 'Changed';
                  const isActive = openDiff?.eventId === advert.event_id;
                  return (
                    <tr
                      key={advert.id}
                      data-testid={`task-advert-row-${advert.id}`}
                      className={`hover:bg-lightGrayColor/40 ${isActive ? 'bg-purpleColor/5' : ''}`}
                    >
                      <td className="py-2 pr-3 text-darkGrayColor font-mono">
                        {advert.id}
                      </td>
                      {eventType === 'all' && (
                        <td className="py-2 pr-3">
                          <span
                            className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
                              advert.event_type === 'Added'
                                ? 'bg-greenColor/15 text-greenColor'
                                : 'bg-blueColor/15 text-darkBlueColor'
                            }`}
                          >
                            {advert.event_type}
                          </span>
                        </td>
                      )}
                      <td className="py-2 pr-3 text-blackColor">
                        <span title={advert.title || ''}>{truncate(advert.title, 60)}</span>
                        {advert.agency_name && (
                          <div className="text-[10px] text-darkGrayColor">
                            {truncate(advert.agency_name, 40)}
                          </div>
                        )}
                      </td>
                      <td className="py-2 pr-3 text-blackColor">
                        {truncate(locationLabel(advert), 40)}
                      </td>
                      <td className="py-2 pr-3 text-right font-medium text-blackColor tabular-nums">
                        {formatPrice(advert.current_price)}
                      </td>
                      <td className="py-2 pr-3 text-darkGrayColor whitespace-nowrap">
                        {formatDate(advert.publication_date)}
                      </td>
                      <td className="py-2 pr-3 text-darkGrayColor whitespace-nowrap">
                        {formatDateTime(advert.event_at)}
                      </td>
                      {(eventType === 'Changed' || eventType === 'all') && (
                        <td className="py-2 pr-3">
                          {isChanged && advert.event_id ? (
                            <button
                              type="button"
                              onClick={() =>
                                setOpenDiff({
                                  eventId: advert.event_id,
                                  advertPreview: {
                                    id: advert.id,
                                    url: advert.url,
                                    title: advert.title,
                                    source: advert.source,
                                  },
                                })
                              }
                              aria-pressed={isActive}
                              data-testid={`task-advert-diff-${advert.event_id}`}
                              title="See what changed"
                              className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] font-medium transition ${
                                isActive
                                  ? 'border-purpleColor bg-purpleColor text-white'
                                  : 'border-purpleColor/50 text-purpleColor hover:bg-purpleColor/10'
                              }`}
                            >
                              <GitCompareArrows size={11} />
                              See changes
                            </button>
                          ) : (
                            <span className="text-darkGrayColor">—</span>
                          )}
                        </td>
                      )}
                      <td className="py-2 pr-0">
                        <a
                          href={advert.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-purpleColor hover:underline"
                          title={advert.url}
                          data-testid={`task-advert-link-${advert.id}`}
                        >
                          Open
                          <ExternalLink size={12} />
                        </a>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between border-t border-gray-200 pt-3 flex-shrink-0">
            <span className="text-xs text-darkGrayColor">
              {from.toLocaleString()}–{to.toLocaleString()} of {total.toLocaleString()}
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={handlePrev}
                disabled={currentPage <= 1 || isFetching}
                data-testid="task-adverts-prev"
                className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-xs text-blackColor hover:bg-lightGrayColor/40 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeft size={14} /> Prev
              </button>
              <span className="text-xs text-darkGrayColor px-2">
                Page {currentPage} / {lastPage}
              </span>
              <button
                type="button"
                onClick={handleNext}
                disabled={currentPage >= lastPage || isFetching}
                data-testid="task-adverts-next"
                className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-xs text-blackColor hover:bg-lightGrayColor/40 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );

  const dialogClasses = openDiff
    ? 'w-[96vw] max-w-3xl transition-all duration-200 lg:mr-[440px]'
    : 'w-[96vw] max-w-5xl transition-all duration-200';

  return (
    <>
      <Dialog
        isOpen={isOpen}
        onClose={onClose}
        classes={dialogClasses}
        content={content}
      />
      <AdvertDiffModal
        isOpen={isOpen && openDiff !== null}
        onClose={() => setOpenDiff(null)}
        token={token}
        eventId={openDiff?.eventId}
        advertPreview={openDiff?.advertPreview}
      />
    </>
  );
};
